import Highcharts from 'highcharts';
import { MAX_LABELS } from './screen-inventory.constants';
import type { InventoryCandidate, InventoryDetail, InventoryLabel } from './screen-inventory.types';
import { isText } from './screen-inventory.utils';

const POINT_NAMED_TYPES = new Set(['pie', 'variablepie', 'treemap', 'sunburst', 'funnel', 'pyramid', 'item', 'wordcloud']);

function dataLength (series: Highcharts.Series): number {
    return (series.options as { data?: unknown[] }).data?.length ?? 0;
}

function describeShape (chart: Highcharts.Chart): string {
    const types = [...new Set(chart.series.map((series) => series.type))];
    const xAxisOptions = chart.xAxis[0]?.options;
    const pointCount = Math.max(0, ...chart.series.map(dataLength));
    const shape = [types.join('/') || 'chart'];

    if (xAxisOptions?.type === 'datetime') shape.push('over time');
    if (xAxisOptions?.categories?.length) shape.push(`across ${xAxisOptions.categories.length} categories`);
    shape.push(`${pointCount} ${pointCount === 1 ? 'point' : 'points'}`, `${chart.series.length} series`);

    return shape.join(', ');
}

function describeAxes (chart: Highcharts.Chart): Pick<InventoryCandidate, 'details'> {
    const x = chart.xAxis[0]?.options.title?.text;
    const y = chart.yAxis[0]?.options.title?.text;
    const details: InventoryDetail[] = [
        ...(isText(x) ? [{ name: 'x-axis', text: x }] : []),
        ...(isText(y) ? [{ name: 'y-axis', text: y }] : []),
    ];

    return details.length ? { details } : {};
}

// Series names come from options, because Highcharts names an unnamed series "Series 1".
function describeLabels (chart: Highcharts.Chart): Pick<InventoryCandidate, 'labelsOf' | 'labels' | 'labelTotal'> {
    const [first] = chart.series;

    if (chart.series.length === 1 && POINT_NAMED_TYPES.has(first.type)) {
        const namedPoints = (first.points ?? []).filter((point) => isText(point.name));
        const labels: InventoryLabel[] = namedPoints
            .slice(0, MAX_LABELS)
            .map((point) => ({ text: point.name, ...(!point.visible && { hidden: true }) }));

        return { labelsOf: 'points', labels, labelTotal: namedPoints.length };
    }

    const named = chart.series.filter((series) => isText(series.options.name));
    const labels = named
        .slice(0, MAX_LABELS)
        .map((series) => ({ text: series.options.name as string, ...(!series.visible && { hidden: true }) }));

    return { labelsOf: 'series', labels, labelTotal: named.length };
}

function describeChart (chart: Highcharts.Chart): InventoryCandidate {
    const { options } = chart;
    const title = options.title?.text;
    const description = options.accessibility?.description || options.caption?.text;
    const fallbackName = chart.series.map((series) => series.options.name).find(isText);

    return {
        element: chart.container,
        kind: 'chart',
        ...(isText(title) && { title }),
        ...(fallbackName && { fallbackName }),
        ...(isText(description) && { description }),
        shape: describeShape(chart),
        ...describeAxes(chart),
        ...describeLabels(chart),
    };
}

export function collectChartCandidates (): InventoryCandidate[] {
    return Highcharts.charts
        .filter((chart): chart is Highcharts.Chart => Boolean(chart?.container?.isConnected))
        .map(describeChart);
}
